import { defineStore } from "pinia";
import router from "../router";
import axios from "axios";

// Construct API base URL safely
const serverUrl = (process.env.VUE_APP_SERVER_URL || "").replace(/\/$/, "");
const serverPort = process.env.VUE_APP_SERVER_PORT;
axios.defaults.baseURL = `${serverUrl}${
  serverPort ? ":" + serverPort : ""
}/api/`;

// SECURITY: Enable sending cookies with cross-origin requests
// Required for httpOnly refresh token cookie
axios.defaults.withCredentials = true;

/**
 * Helper function to decode JWT token and extract expiration time
 * @param {string} token - JWT token
 * @returns {number} - Expiration timestamp in milliseconds
 */
function parseJwtExpiration(token) {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return Date.now() + 3600000; // Default 1 hour

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );

    const decoded = JSON.parse(jsonPayload);
    // JWT exp is in seconds, convert to milliseconds
    return decoded.exp ? decoded.exp * 1000 : Date.now() + 3600000;
  } catch (error) {
    console.error("Error decoding JWT token:", error);
    // Fallback to 1 hour if decoding fails
    return Date.now() + 3600000;
  }
}

export const useAuthStore = defineStore({
  id: "auth",
  state: () => ({
    // User info from localStorage (not sensitive)
    user: (() => { try { return JSON.parse(localStorage.getItem("user")); } catch { localStorage.removeItem("user"); return null; } })(),
    // SECURITY: Access token stored in MEMORY ONLY - not accessible via XSS localStorage theft
    accessToken: null,
    accessTokenExpiresAt: null,
    // SECURITY: Refresh token is in httpOnly cookie - cannot be accessed by JavaScript
    // hasSession indicates if we believe a valid session exists (user data in localStorage)
    hasSession: !!localStorage.getItem("user"),
    // Seconds of inactivity before logout, as configured on the server (SESSION_IDLE_TIMEOUT_MINUTES)
    sessionIdleTimeout: Number(localStorage.getItem("sessionIdleTimeout")) || null,
    isRefreshing: false,
    isInitialized: false, // Flag to track if auth has been initialized
    hasDelegation: false, // true if the user has at least one TranslationDelegation record
    userDelegations: [],  // full delegation objects [{ id, translationGroup, language, sourceLanguage }]
  }),

  actions: {
    /**
     * Get token expiration from JWT payload
     * @param {string} token - JWT token
     * @returns {number} - Expiration timestamp
     */
    getTokenExpiration(token) {
      if (!token) return Date.now() + 3600000;
      return parseJwtExpiration(token);
    },

    setSessionIdleTimeout(seconds) {
      if (!seconds) return;
      this.sessionIdleTimeout = seconds;
      localStorage.setItem("sessionIdleTimeout", String(seconds));
    },

    /**
     * Initialize authentication on app startup
     * Try to get new access token using httpOnly refresh cookie
     */
    async initializeAuth() {
      if (this.isInitialized) return;

      // MIGRATION: Remove old tokens from localStorage (one-time cleanup)
      if (localStorage.getItem("accessToken")) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("accessTokenExpiresAt");
      }
      if (localStorage.getItem("refreshToken")) {
        localStorage.removeItem("refreshToken");
      }

      try {
        // If user data exists, try to refresh using httpOnly cookie
        if (this.hasSession) {
          await this.refreshAccessToken();
        }
      } catch (error) {
        console.error("Auto-login failed:", error.response?.status);
        // Clear invalid session
        this.clearLocalStorage();
      } finally {
        this.isInitialized = true;
      }
    },

    async login(username, password) {
      if (this.accessToken || this.hasSession) {
        // User already logged in - redirect directly without DOM manipulation
        console.warn(
          "Login attempt while already authenticated. Redirecting to dashboard.",
        );
        router.push("/");
        return;
      }

      const response = await axios.post("/login", {
        username: username,
        password: password,
      });

      // update pinia state
      // Note: refresh_token is now set as httpOnly cookie by backend
      const { token, user, session_idle_timeout } = response.data;

      this.accessToken = token;
      this.user = user;
      this.hasSession = true;
      this.setSessionIdleTimeout(session_idle_timeout);

      // Decode JWT to get actual expiration time from token (more secure)
      this.accessTokenExpiresAt = this.getTokenExpiration(token);

      // SECURITY: Only store non-sensitive data in localStorage
      // Access token in memory, refresh token in httpOnly cookie
      localStorage.setItem("user", JSON.stringify(user));

      // Load companies and set keyUserTypes BEFORE redirecting
      await this.loadInitialCompanyRoles();
      await this.fetchDelegations();

      // redirect to previous url or default to home page
      router.push("/");
    },

    async requestLoginLink(username, language) {
      await axios.post("/request_login_link", {
        username: username,
        locale: language,
      });
    },

    async loginPasswordlessCheck(username, expires, hash) {
      if (this.accessToken || this.hasSession) {
        // User already logged in - redirect directly without DOM manipulation
        console.warn(
          "Passwordless login attempt while already authenticated. Redirecting.",
        );
        router.push("/");
        return;
      }

      const bodyFormData = new FormData();
      bodyFormData.append("user", username);
      bodyFormData.append("expires", expires);
      bodyFormData.append("hash", hash);

      const response = await axios.post("/login_check", bodyFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // update pinia state
      // Note: refresh_token is now set as httpOnly cookie by backend
      const { token, user, session_idle_timeout } = response.data;

      this.accessToken = token;
      this.user = user;
      this.hasSession = true;
      this.setSessionIdleTimeout(session_idle_timeout);

      // Decode JWT to get actual expiration time from token (more secure)
      this.accessTokenExpiresAt = this.getTokenExpiration(token);

      // SECURITY: Only store non-sensitive data in localStorage
      // Access token in memory, refresh token in httpOnly cookie
      localStorage.setItem("user", JSON.stringify(user));

      // Load companies and set keyUserTypes BEFORE redirecting
      // This ensures router guard has access to proper roles
      await this.loadInitialCompanyRoles();
      await this.fetchDelegations();

      // redirect to previous url or default to home page
      router.push("/");
    },

    /**
     * Load initial company and roles after login
     * This ensures keyUserTypes are available before routing happens
     */
    async fetchDelegations() {
      if (!this.user?.user_id || !this.accessToken) return;
      try {
        const res = await axios.get("translation_delegations", {
          params: { user: this.user.user_id, pagination: false },
          headers: { Authorization: `Bearer ${this.accessToken}` },
        });
        const items = res.data["hydra:member"] ?? res.data;
        this.userDelegations = Array.isArray(items) ? items : [];
        this.hasDelegation = this.userDelegations.length > 0;
      } catch {
        this.hasDelegation = false;
        this.userDelegations = [];
      }
    },

    async loadInitialCompanyRoles() {
      try {
        const { useCompanyStore } = await import("@/stores/companyStore");
        const companyStore = useCompanyStore();

        // Import axiosInstance which has the access token in headers
        const axiosInstance = (await import("@/services/AxiosTokenInstance"))
          .default;

        // Fetch user's companies using authenticated instance
        const response = await axiosInstance.get("ous", {
          params: {
            itemsPerPage: 100,
            page: 1,
            "order[name]": "ASC",
          },
        });

        const companies = response.data["hydra:member"];

        if (companies && companies.length > 0) {
          const firstCompany = companies[0];

          // Set company in store BEFORE fetching roles
          companyStore.selectCompany(firstCompany);

          // Fetch latest roles from API instead of using cached keyUsers
          await companyStore.fetchCompanyRoles(
            firstCompany.id,
            this.user.user_id,
          );
        }
      } catch (error) {
        console.error("Error loading initial company roles:", error);
        // Continue with login even if this fails
      }
    },

    async refreshAccessToken() {
      // Prevent concurrent refresh attempts
      if (this.isRefreshing) {
        // Wait for the current refresh to complete
        while (this.isRefreshing) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        return this.accessToken;
      }

      // Skip refresh on login pages
      const currentRoute = router.currentRoute.value.name;
      if (
        currentRoute === "login_passwordless" ||
        currentRoute === "login" ||
        currentRoute === "login_passwordless_check"
      ) {
        return;
      }

      try {
        this.isRefreshing = true;

        // SECURITY: Refresh token is sent automatically via httpOnly cookie
        // No need to send it in request body
        const response = await axios.post("token/refresh1", {});

        // update pinia state
        // Note: new refresh_token is set as httpOnly cookie by backend
        const { token, user, session_idle_timeout } = response.data;

        this.accessToken = token;
        this.user = user;
        this.hasSession = true;
        this.setSessionIdleTimeout(session_idle_timeout);

        // Decode JWT to get actual expiration time from token (more secure)
        this.accessTokenExpiresAt = this.getTokenExpiration(token);

        // SECURITY: Only store non-sensitive data in localStorage
        // Access token in memory, refresh token in httpOnly cookie
        localStorage.setItem("user", JSON.stringify(user));

        await this.fetchDelegations();

        return this.accessToken;
      } catch (error) {
        console.error("Refresh token failed:", error);

        // Only logout if it's actually a refresh token error, not a network error -
        // a network blip must not leave the app half logged out
        if (error.response?.status === 401 || error.response?.status === 403) {
          this.user = null;
          this.accessToken = null;
          this.hasSession = false;
          this.accessTokenExpiresAt = null;
          // Clear localStorage immediately
          this.clearLocalStorage();
          // Redirect to login without calling logout to prevent recursion
          const locale = localStorage.getItem("selectedLanguage") || "en";
          router.push({
            name: "login_passwordless",
            params: { locale: locale },
          });
        }
        throw error;
      } finally {
        this.isRefreshing = false;
      }
    },

    async logout() {
      // Invalidate the refresh token server-side and clear its httpOnly cookie
      // (the server also records the user's last activity here)
      if (this.hasSession) {
        try {
          await axios.post("token/refresh1/invalidate");
        } catch (error) {
          console.error("Error invalidating refresh token during logout:", error);
          // Continue with logout even if invalidation fails
        }
      }
      const locale = localStorage.getItem("selectedLanguage") || "en";
      // Set a specific key in localStorage to trigger logout in other tabs
      localStorage.setItem("logout", Date.now());
      router.push({ name: "login_passwordless", params: { locale: locale } });
      this.clearLocalStorage();
    },

    async clearLocalStorage() {
      // Clear state first
      this.user = null;
      this.accessToken = null;
      this.hasSession = false;
      this.accessTokenExpiresAt = null;

      // Clear company store data
      try {
        const { useCompanyStore } = await import("@/stores/companyStore");
        const companyStore = useCompanyStore();
        companyStore.clearCompanyData();
      } catch (error) {
        console.error("Error clearing company data:", error);
      }

      // Note: accessToken is memory only, refreshToken is httpOnly cookie (cleared by backend)
      const itemsToRemove = [
        "user",
        "columnFiltersForProjects",
        "hasAcceptedInstructions",
        "selectedCompany",
        "keyUserTypes",
        "lastActivityAt",
        "sessionIdleTimeout",
      ];

      itemsToRemove.forEach((item) => localStorage.removeItem(item));
    },

    async checkAuthorization() {
      const accessTokenExpiresAt = this.accessTokenExpiresAt;
      const isAccessTokenExpired = Date.now() >= accessTokenExpiresAt;

      if (isAccessTokenExpired && this.hasSession) {
        //console.log("Refreshing access token...");
        try {
          await this.refreshAccessToken();
        } catch (error) {
          console.error(error);
          // Handle the error (e.g., redirect to login page)
        }
      }
    },
  },
});

// Add an event listener for the `storage` event to handle logout across tabs
window.addEventListener("storage", (event) => {
  if (event.key === "logout") {
    const authStore = useAuthStore();
    const locale = localStorage.getItem("selectedLanguage") || "en";
    router.push({ name: "login_passwordless", params: { locale: locale } });
    authStore.clearLocalStorage();
  }
});
